import React from 'react';
import { daysBetween, fmtDateShort } from '../utils/format';

/**
 * ExpirationBar — a tiny horizontal progress bar showing how much of a
 * contract's term has elapsed. All detail is exposed via a native
 * `title` tooltip on hover; the row stays visually clean. Colors:
 *
 *   green   > 60 days left
 *   yellow  ≤ 60 days left
 *   red     ≤ 14 days left, or already expired
 *   gray    unknown / no expiration set
 *
 * Fill width is clamped 0–100% of elapsed time. No effectiveDate → we
 * default the fill to the "halfway" position so the bar still renders
 * without implying a specific progress value.
 */
export default function ExpirationBar({ date, effective, today = new Date(), className = '' }) {
  const cls = `exp-bar${className ? ` ${className}` : ''}`;
  if (!date) {
    return (
      <span className={cls} title="No expiration date set">
        <span className="track">
          <span className="fill gray" style={{ width: '100%', opacity: 0.25 }} />
        </span>
      </span>
    );
  }

  const remaining = daysBetween(today, date);
  const elapsed = effective ? daysBetween(effective, today) : null;
  const total = effective ? daysBetween(effective, date) : null;
  const pct = total && total > 0
    ? Math.max(0, Math.min(100, (elapsed / total) * 100))
    : (remaining <= 0 ? 100 : 50);

  let tone = 'green';
  if (remaining <= 0) tone = 'red';
  else if (remaining <= 14) tone = 'red';
  else if (remaining <= 60) tone = 'yellow';

  const tooltip = remaining <= 0
    ? `Expired ${fmtDateShort(date)}`
    : `Expires ${fmtDateShort(date)} · ${remaining} day${remaining === 1 ? '' : 's'} left`;

  return (
    <span className={cls} title={tooltip}>
      <span className="track">
        <span className={`fill ${tone}`} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}
